// Libraries
import React from 'react';

// Supermove
import {Styled, Space, Icon} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useQuery, useResponsive, useToast} from '@supermove/hooks';
import {LeadProviderEndpoint, ProjectTypeModel} from '@supermove/models';

import DrawerWithAction from '@shared/design/components/Drawer/DrawerWithAction';
import DropdownInput from '@shared/design/components/DropdownInput';
import FieldInput from '@shared/design/components/Field/FieldInput';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import useUpdateLeadProviderEndpointMutation from 'modules/Organization/Settings/Company/components/LeadProvider/useUpdateLeadProviderEndpointMutation';

const Container = Styled.View``;

type Form = ReturnType<typeof useUpdateLeadProviderEndpointMutation>['form'];

const FormRoot = 'updateLeadProviderEndpointForm';

const UpdateLeadProviderEndpointDrawer = ({
  endpoint,
  projectTypes,
  isOpen,
  handleClose,
  refetch,
}: {
  endpoint: LeadProviderEndpoint;
  projectTypes: ProjectTypeModel[];
  isOpen: boolean;
  handleClose: () => void;
  refetch: ReturnType<typeof useQuery>['refetch'];
}) => {
  const responsive = useResponsive();
  const successToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Changes saved.',
  });

  const {form, submitting, handleSubmit} = useUpdateLeadProviderEndpointMutation({
    endpoint,
    onSuccess: () => {
      successToast.handleToast();
      form.resetForm();
      handleClose();
      refetch();
    },
    onError: (errors) => {
      console.error({errors});
    },
  });

  let canSubmit =
    !!form.values.updateLeadProviderEndpointForm.name &&
    !!form.values.updateLeadProviderEndpointForm.description;
  if (endpoint.leadProviderGlobalIntegration?.requiresProjectType) {
    canSubmit =
      canSubmit &&
      !!form.values.updateLeadProviderEndpointForm.globalFields?.projectTypeId &&
      !!form.values.updateLeadProviderEndpointForm.globalFields!.jobTypeId;
  }

  return (
    <DrawerWithAction
      isOpen={isOpen}
      handleClose={handleClose}
      headerText={'Edit Lead Provider'}
      primaryActionText={'Save'}
      primaryActionIcon={Icon.Check}
      isSubmitting={submitting}
      isDisabled={!canSubmit}
      handleSubmit={handleSubmit}
    >
      <Container responsive={responsive}>
        <FieldsNameDescription form={form} />
        {endpoint.leadProviderGlobalIntegration?.requiresProjectType && (
          <FieldsProjectType form={form} projectTypes={projectTypes} />
        )}
      </Container>
    </DrawerWithAction>
  );
};

const FieldsProjectType = ({
  form,
  projectTypes,
}: {
  form: Form;
  projectTypes: ProjectTypeModel[];
}) => {
  const projectType = projectTypes.find(
    (projectType) =>
      projectType.id === form.values.updateLeadProviderEndpointForm.globalFields!.projectTypeId,
  );

  return (
    <React.Fragment>
      <Space height={16} />
      <FieldInput
        {...form}
        component={DropdownInput}
        name={`${FormRoot}.globalFields.projectTypeId`}
        label={'Project Type'}
        input={{
          placeholder: 'Select project type',
          options: projectTypes.map((projectType) => {
            return {
              label: projectType.name,
              value: projectType.id,
            };
          }),
          setFieldValue: (label: string, projectTypeId: string) => {
            form.setFieldValue(`${FormRoot}.globalFields.jobTypeId`, null);
            form.setFieldValue(`${FormRoot}.globalFields.projectTypeId`, projectTypeId);
          },
          style: {width: '100%'},
          isPortaled: true,
        }}
        isResponsive
        isRequired
      />
      <Space height={16} />
      <FieldInput
        {...form}
        component={DropdownInput}
        name={`${FormRoot}.globalFields.jobTypeId`}
        label={'Job Type'}
        input={{
          placeholder: 'Select job type',
          options:
            projectType?.jobTypes.map((jobType) => {
              return {
                label: jobType.name,
                value: jobType.id,
              };
            }) || [],
          setFieldValue: (label: string, jobTypeId: string) => {
            form.setFieldValue(`${FormRoot}.globalFields.jobTypeId`, jobTypeId);
          },
          isDisabled: !projectType,
          style: {width: '100%'},
          isPortaled: true,
        }}
        isResponsive
        isRequired
      />
    </React.Fragment>
  );
};

const FieldsNameDescription = ({form}: {form: Form}) => {
  return (
    <React.Fragment>
      <FieldInput
        {...form}
        name={`${FormRoot}.name`}
        label={'Name'}
        input={{
          placeholder: 'Enter name',
        }}
        isResponsive
        isRequired
      />
      <Space height={16} />
      <FieldInput
        {...form}
        name={`${FormRoot}.description`}
        label={'Description'}
        input={{
          placeholder: 'Enter description',
        }}
        isResponsive
        isRequired
      />
    </React.Fragment>
  );
};

UpdateLeadProviderEndpointDrawer.fragment = gql`
  ${useUpdateLeadProviderEndpointMutation.fragment}
  fragment UpdateLeadProviderEndpointDrawer on Organization {
    id
    projectTypes: projectTypesForCategory(category: "MOVE") {
      id
      name
      jobTypes {
        id
        name
      }
    }
    leadProviderEndpoints {
      ...useUpdateLeadProviderEndpointMutation
      leadProviderGlobalIntegration {
        id
        requiresProjectType
      }
    }
  }
`;

export default UpdateLeadProviderEndpointDrawer;
